/**
 * A study room: themed shell, live orientation header, exit back to the lobby. The
 * dashboards and the ward live in DashboardRoom once the study file has loaded.
 *
 * A room is a study and a three character room code (2026-10-06): the network joins
 * `<study>.<code>`, so only people who entered the same code are in it.
 */
import * as THREE from 'three';
import { roomKey, type Query, type StudyFile } from '@clinspace/shared';
import { DashboardRoom } from '../dashboards/DashboardRoom.ts';
import { setUiMode } from '../dashboards/charts.ts';
import { api, getJson } from '../lib/api.ts';
import { ROOM, STUDIO_FITTINGS, buildStudyRoom, type StudyRoomWorld } from '../world/StudyRoom.ts';
import { STUDIO } from '../world/plan.ts';
import type { AppContext, SceneController, TeleportAnchor } from './Scene.ts';
import { WARD } from '../ward/layout.ts';

type RoomData = [StudyFile, { items: Query[] }];

function fetchRoomData(code: string): Promise<RoomData> {
  return Promise.all([
    // The study file is up to 3 MB; over a tunnel to the Quest it needs more than the default.
    getJson<StudyFile>(api('studies', code), undefined, 30_000),
    getJson<{ items: Query[] }>(api('queries'), { study: code, limit: 5000 }, 30_000),
  ]);
}

/** Room data fetched in the lobby while the room code is typed, by study. */
const early = new Map<string, { at: number; data: Promise<RoomData> }>();
/** Older than this, an early fetch is dropped: queries may have moved on. */
const EARLY_MAX_AGE_MS = 60_000;

/**
 * Starts loading a study's file and queries before its room is entered (2026-10-06), so the
 * dashboards, and Luma with them, are ready sooner. Used once, by the next room of the study.
 */
export function prefetchRoomData(code: string): void {
  const held = early.get(code);
  if (held && performance.now() - held.at < EARLY_MAX_AGE_MS) return;
  const data = fetchRoomData(code);
  data.catch(() => early.delete(code));
  early.set(code, { at: performance.now(), data });
}

function roomData(code: string): Promise<RoomData> {
  const held = early.get(code);
  early.delete(code);
  return held && performance.now() - held.at < EARLY_MAX_AGE_MS
    ? held.data.catch(() => fetchRoomData(code))
    : fetchRoomData(code);
}

export class RoomScene implements SceneController {
  readonly name: string;
  readonly group = new THREE.Group();
  readonly canLeave = true;
  readonly anchors: TeleportAnchor[];
  private world: StudyRoomWorld;
  private unsubscribeRooms: (() => void) | undefined;
  private unsubscribeMembers: (() => void) | undefined;
  private savedEnvironment: THREE.Scene['environment'] = null;
  private savedBackground: THREE.Scene['background'] = null;
  private savedFog: THREE.Scene['fog'] = null;
  /** The study file once loaded (the data horizon shows its headline numbers). */
  private file: StudyFile | undefined;
  private unsubscribeKeys: (() => void) | undefined;
  private leaving = false;
  private lightPulse = 0;
  /** Live dashboards; undefined until the study file has loaded. */
  dashboards: DashboardRoom | undefined;
  loadError: string | undefined;
  /** The network room: the study and the room code. */
  readonly key: string;
  readonly label: string;
  /** Whether the centre screen's room line has heard about transcripts yet. */
  private infoShown = false;

  constructor(
    private readonly ctx: AppContext,
    readonly code: string,
    readonly roomCode: string,
  ) {
    // Study rooms are the warm chamber; set before any panel is built or drawn.
    setUiMode('chamber');
    this.name = `room:${code}`;
    this.key = roomKey(code, roomCode);
    this.label = `${code} · room ${roomCode}`;
    const study = ctx.manifest.studies.find((s) => s.code === code);
    if (!study) throw new Error(`Unknown study ${code}`);
    this.world = buildStudyRoom(
      {
        code: study.code,
        title: study.title,
        shortTitle: study.shortTitle,
        phase: study.phase,
        therapeuticArea: study.therapeuticArea,
        accent: study.accentColour,
      },
      ctx.renderer,
      // The manifest knows the study's size before its file loads: the ward room fits it.
      // Dev only: ?wardRoom=1000 builds the room for that many subjects, to see a big ward.
      (import.meta.env.DEV && Number(new URLSearchParams(location.search).get('wardRoom'))) ||
        study.subjectCount,
    );
    this.group.add(this.world.group);
    this.anchors = [
      {
        label: 'Table',
        position: new THREE.Vector3(ROOM.tableCentre.x, 0, ROOM.tableCentre.z + 2.6),
      },
      { label: 'Ward', position: new THREE.Vector3(WARD.entrance.x, 0, WARD.entrance.z) },
      // In the studio, in front of the whiteboard.
      {
        label: 'Studio',
        position: new THREE.Vector3(STUDIO.x0 + 2.0, 0, STUDIO_FITTINGS.boardZ),
      },
      {
        label: 'Lectern',
        // Behind the lectern, on the wall side where its top faces, facing the audience.
        position: new THREE.Vector3(
          this.world.lecternPosition.x,
          0,
          this.world.lecternPosition.z + 0.6,
        ),
      },
    ];
  }

  enter(): void {
    const { ctx } = this;
    ctx.locomotion.bounds = this.world.bounds;
    ctx.locomotion.floors = this.world.floors;
    ctx.rig.group.position.set(ROOM.spawn.x, ctx.rig.heightOffset, ROOM.spawn.z);
    ctx.rig.faceTowards(ROOM.lookAt, ctx.inXr());
    // The chamber's background, without fog; restored on exit. No scene-wide environment
    // map: it made every lit material sample reflections and cost ~70 ms a frame in the
    // software renderer (fill rate is the Quest's limit too).
    this.savedEnvironment = ctx.scene.environment;
    this.savedBackground = ctx.scene.background;
    this.savedFog = ctx.scene.fog;
    ctx.scene.background = new THREE.Color('#2a2622');
    ctx.scene.fog = null;
    ctx.net.join(this.key);
    ctx.audio.setHum(true, 0.025);
    this.leaving = false;
    this.unsubscribeRooms = ctx.net.onRooms(() => this.updateHeader());
    this.unsubscribeMembers = ctx.net.onMembers(() => this.updateHeader());
    this.unsubscribeKeys = ctx.input.onKey((e) => {
      if (this.dashboards?.handleKeyEvent(e)) {
        e.preventDefault();
        return;
      }
      // Keys belong to a text field or the controls list (main.ts) while captured: Esc there
      // closes what is open, never the room.
      if (ctx.input.captureText) return;
      // Only L leaves (2026-10-07, the user: a second Esc to free the mouse took them out of the
      // room by surprise). Esc frees the mouse and closes what is open, nothing more.
      if (e.code === 'KeyL') this.leave();
    });
    this.updateHeader();
    void this.loadDashboards();
  }

  private async loadDashboards(): Promise<void> {
    const { ctx } = this;
    try {
      const [file, queries] = await roomData(this.code);
      if (this.leaving || this.dashboards) return;
      this.dashboards = new DashboardRoom(ctx, file, this.world, queries.items, this.roomCode);
      this.group.add(this.dashboards.group);
      this.file = file;
      this.updateHeader();
      console.log(
        `[dashboards] ${this.code}: ${this.dashboards.dashboards.length} dashboards, ${queries.items.length} queries`,
      );
    } catch (err) {
      this.loadError = err instanceof Error ? err.message : String(err);
      console.error(`[dashboards] failed to load ${this.code}: ${this.loadError}`);
      this.updateHeader();
    }
  }

  exit(): void {
    this.ctx.scene.environment = this.savedEnvironment;
    this.ctx.scene.environmentIntensity = 1;
    this.ctx.scene.background = this.savedBackground;
    this.ctx.scene.fog = this.savedFog;
    this.world.dispose();
    this.unsubscribeRooms?.();
    this.unsubscribeMembers?.();
    this.unsubscribeKeys?.();
    this.ctx.audio.setHum(false);
    if (this.dashboards) {
      this.group.remove(this.dashboards.group);
      this.dashboards.dispose();
      this.dashboards = undefined;
    }
  }

  update(dt: number): void {
    this.world.update(dt);
    if (this.dashboards) this.dashboards.update(dt);
    if (!this.infoShown && this.dashboards?.assistantInfo) {
      this.infoShown = true;
      this.updateHeader();
    } else {
      this.lightPulse += dt;
      const pulse = 0.9 + Math.sin(this.lightPulse * 2) * 0.3;
      for (const p of this.world.panels)
        (p.statusLight.material as THREE.MeshStandardMaterial).emissiveIntensity = pulse;
    }
    if (!this.leaving) this.checkExitZone();
  }

  /**
   * The data horizon, the centre screen's room line, and the loading or error line where the
   * centre screen will stand. Always from the latest state: the room summaries the network
   * holds now (never a list captured earlier: that left the old entrance board at "0 in room"
   * once the study file finished loading) and the members of this room as the server last
   * sent them.
   */
  private updateHeader(): void {
    const { ctx } = this;
    const rooms = ctx.net.rooms.length ? ctx.net.rooms : ctx.rooms;
    const room = rooms.find((r) => r.code === this.code);
    // Only this room's people: the study's other room codes are other groups.
    const people =
      ctx.net.currentRoom === this.key
        ? ctx.net.members.map((m) => ({ name: m.name, role: m.role }))
        : [];
    this.world.setHeader({
      enrolled: room?.enrolled ?? this.file?.dashboards.header.enrolled ?? 0,
      target: room?.target ?? this.file?.dashboards.header.target ?? 0,
      openQueries: room?.openQueries ?? 0,
      lastDataCut: this.ctx.manifest.anchorDate,
      siteCount: room?.siteCount,
      study: this.file,
    });
    this.world.setStatus(
      this.loadError
        ? `Could not load the study: ${this.loadError}. Press L to return to the lobby.`
        : this.dashboards
          ? undefined
          : 'Loading study data…',
      !!this.loadError,
    );
    this.dashboards?.setRoomInfo({
      code: this.roomCode,
      people,
      voiceTranscribed: !!this.dashboards.assistantInfo?.transcripts,
    });
  }

  private checkExitZone(): void {
    const feet = this.ctx.rig.feetPosition(new THREE.Vector3());
    const z = this.world.exitZone;
    if (feet.x > z.minX && feet.x < z.maxX && feet.z > z.minZ && feet.z < z.maxZ) this.leave();
  }

  private leave(): void {
    if (this.leaving) return;
    this.leaving = true;
    this.dashboards?.filterEditor.hide();
    this.ctx.audio.doorSlide();
    this.ctx.returnToLobby();
  }
}
